import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Link2, Image as ImageIcon, Video, StickyNote, File } from 'lucide-react';
import { Item } from '@/stores/canvasStore';

interface MindBubbleItemProps {
  item: Item;
  onDragEnd: (position: { x: number; y: number }) => void;
}

// Color map for sticky notes
const STICKY_COLORS: Record<string, string> = {
  yellow: '#fef08a',
  blue: '#93c5fd',
  pink: '#f9a8d4',
  green: '#86efac',
  purple: '#c4b5fd',
};

export function MindBubbleItem({ item, onDragEnd }: MindBubbleItemProps) {
  const [isDragging, setIsDragging] = useState(false);
  
  const getItemPreview = () => {
    const content = item.content as Record<string, unknown>;
    
    switch (item.item_type) {
      case 'sticky': {
        const color = (content.color as string) || 'yellow';
        return (
          <div 
            className="w-full h-full rounded-lg flex items-center justify-center"
            style={{ backgroundColor: STICKY_COLORS[color] || STICKY_COLORS.yellow }}
          >
            <StickyNote className="w-5 h-5 text-gray-700 opacity-60" />
          </div>
        );
      }
      
      case 'image': {
        const url = content.url as string;
        return url ? (
          <img 
            src={url} 
            alt="" 
            className="w-full h-full object-cover rounded-lg"
            draggable={false}
          />
        ) : (
          <div className="w-full h-full rounded-lg bg-muted flex items-center justify-center">
            <ImageIcon className="w-5 h-5 text-muted-foreground" />
          </div>
        );
      }
      
      case 'video': {
        const thumbnailUrl = content.thumbnailUrl as string;
        return thumbnailUrl ? (
          <div className="relative w-full h-full">
            <img 
              src={thumbnailUrl} 
              alt="" 
              className="w-full h-full object-cover rounded-lg"
              draggable={false}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <Video className="w-5 h-5 text-white drop-shadow-lg" />
            </div>
          </div>
        ) : (
          <div className="w-full h-full rounded-lg bg-muted flex items-center justify-center">
            <Video className="w-5 h-5 text-muted-foreground" />
          </div>
        );
      }
      
      case 'text_card':
      case 'document': {
        const title = (content.title as string) || (content.body as string);
        return (
          <div className="w-full h-full rounded-lg bg-card border border-border flex items-center justify-center p-1">
            <span className="text-[8px] text-muted-foreground text-center line-clamp-2 leading-tight">
              {title ? title.slice(0, 20) : 'Note'}
            </span>
          </div>
        );
      }
      
      case 'link': {
        const image = content.image as string;
        return image ? (
          <div className="relative w-full h-full">
            <img 
              src={image} 
              alt="" 
              className="w-full h-full object-cover rounded-lg"
              draggable={false}
            />
            <div className="absolute bottom-0 right-0 p-0.5">
              <Link2 className="w-3 h-3 text-white drop-shadow-lg" />
            </div>
          </div>
        ) : (
          <div className="w-full h-full rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center">
            <Link2 className="w-5 h-5 text-blue-500" />
          </div>
        );
      }
      
      case 'file':
        return (
          <div className="w-full h-full rounded-lg bg-muted flex items-center justify-center">
            <File className="w-5 h-5 text-muted-foreground" />
          </div>
        );
      
      default:
        return (
          <div className="w-full h-full rounded-lg bg-muted flex items-center justify-center">
            <FileText className="w-5 h-5 text-muted-foreground" />
          </div>
        );
    }
  };
  
  return (
    <motion.div
      drag
      dragSnapToOrigin
      onDragStart={() => setIsDragging(true)}
      onDragEnd={(e, info) => {
        setIsDragging(false);
        // Get drop position relative to viewport
        const dropPosition = {
          x: info.point.x,
          y: info.point.y
        };
        onDragEnd(dropPosition);
      }}
      whileHover={{ scale: 1.1 }}
      whileDrag={{ scale: 1.2, zIndex: 100 }}
      className={`
        w-16 h-16 
        cursor-grab active:cursor-grabbing 
        rounded-lg overflow-hidden
        shadow-sm hover:shadow-md transition-shadow
        ${isDragging ? 'ring-2 ring-primary' : ''}
      `}
    >
      {getItemPreview()}
    </motion.div>
  );
}
